// Uyarlanmış kopya — emsal: beltasapp/frontend/src/shared/ui/izgara/izgara-secim.ts
// Fark: İngilizce adlar; Ctrl+Ok/PageUp/gizli satır yok (bu ızgarada gerekmiyor); Tab sınır bilgisi eklendi.
import type { Coord, Range } from './types'

export function rangeOf(a: Coord, b: Coord): Range {
  return {
    r0: Math.min(a.row, b.row),
    r1: Math.max(a.row, b.row),
    c0: Math.min(a.col, b.col),
    c1: Math.max(a.col, b.col),
  }
}

export function inRange(range: Range, row: number, col: number): boolean {
  return row >= range.r0 && row <= range.r1 && col >= range.c0 && col <= range.c1
}

export function isSingleCell(range: Range): boolean {
  return range.r0 === range.r1 && range.c0 === range.c1
}

/** Clamps a coordinate into the grid (an empty grid clamps to 0,0). */
export function clampCoord(c: Coord, rowCount: number, colCount: number): Coord {
  return {
    row: Math.min(Math.max(c.row, 0), Math.max(rowCount - 1, 0)),
    col: Math.min(Math.max(c.col, 0), Math.max(colCount - 1, 0)),
  }
}

export function clampRange(r: Range, rowCount: number, colCount: number): Range {
  const a = clampCoord({ row: r.r0, col: r.c0 }, rowCount, colCount)
  const b = clampCoord({ row: r.r1, col: r.c1 }, rowCount, colCount)
  return rangeOf(a, b)
}

/** Arrow-key target: stops at the edge (no wrap, like Excel). */
export function moveTarget(
  from: Coord,
  dRow: number,
  dCol: number,
  rowCount: number,
  colCount: number,
): Coord {
  return clampCoord({ row: from.row + dRow, col: from.col + dCol }, rowCount, colCount)
}

/**
 * Tab target: wraps to the next row's start (Shift+Tab: previous row's end). The result is NOT
 * clamped vertically so the caller can tell "ran off the end": `row === rowCount` or `row === -1`.
 */
export function tabTarget(from: Coord, back: boolean, colCount: number): Coord {
  const col = from.col + (back ? -1 : 1)
  if (col < 0) return { row: from.row - 1, col: colCount - 1 }
  if (col > colCount - 1) return { row: from.row + 1, col: 0 }
  return { row: from.row, col }
}

/** Is this the grid's last cell (Tab) / first cell (Shift+Tab)? Focus then leaves the grid. */
export function atTabEdge(from: Coord, back: boolean, rowCount: number, colCount: number): boolean {
  return back
    ? from.row <= 0 && from.col <= 0
    : from.row >= rowCount - 1 && from.col >= colCount - 1
}

/** Home / End / Ctrl+Home / Ctrl+End. */
export function homeEndTarget(
  from: Coord,
  end: boolean,
  ctrl: boolean,
  rowCount: number,
  colCount: number,
): Coord {
  const col = end ? Math.max(colCount - 1, 0) : 0
  if (!ctrl) return { row: from.row, col }
  return { row: end ? Math.max(rowCount - 1, 0) : 0, col }
}

export function fullRange(rowCount: number, colCount: number): Range {
  return { r0: 0, r1: Math.max(rowCount - 1, 0), c0: 0, c1: Math.max(colCount - 1, 0) }
}

export function rowRange(row: number, colCount: number): Range {
  return { r0: row, r1: row, c0: 0, c1: Math.max(colCount - 1, 0) }
}

export function columnRange(col: number, rowCount: number): Range {
  return { r0: 0, r1: Math.max(rowCount - 1, 0), c0: col, c1: col }
}

export function cellKey(rowKey: string, columnId: string): string {
  return `${rowKey}\u0000${columnId}`
}
